import { Formik } from "formik";

import { useAddPricingMutation } from "@sso/apis/pricing";
import { useGetSoftwareLicenceById } from "@sso/apis/softwareLicences/hooks";
import { DialogActionButtons, DialogBody, DialogHeader } from "@sso/components/Dialogs/components";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectPricingSoftwareLicenceId } from "@sso/store/slices/pricing";
import {
  LicenceType,
  PricingCreateRequest,
  PricingFormModel,
  PricingKeys,
  PricingSummaryKeys,
  SoftwareLicenceSummaryKeys,
} from "@sso/types";
import { CustomForm } from "@sso/ui/CustomForm";

import PricingForm from "./pricingForm";
import { pricingSchemeValidation } from "./schema";

const PricingDialog = () => {
  const softwareLicenceId = useAppSelector(selectPricingSoftwareLicenceId);
  const softwareLicence = useGetSoftwareLicenceById(softwareLicenceId);

  const [addPricing, { isLoading: addLoading }] = useAddPricingMutation();

  const onSubmit = (values: PricingFormModel) => {
    if (softwareLicence![SoftwareLicenceSummaryKeys.LICENCE_TYPE] === LicenceType.API) {
      values.pricingRules = values.apiPricingRules?.reduce((acc: any, curr: any) => {
        const pricingRules = curr.pricingRules.map((rule: any) => {
          return { ...rule, billableOperationId: curr.billableOperationId };
        });
        return [...acc, ...pricingRules];
      }, []);
    }

    const request = {
      [PricingSummaryKeys.DATE_OF_EFFECT]: values[PricingSummaryKeys.DATE_OF_EFFECT],
      [PricingKeys.PRICING_RULES]: values[PricingKeys.PRICING_RULES]?.map(rule => ({
        ...rule,
        currencyCode: "GBP",
      })),
      softwareLicenceId: softwareLicenceId,
    } as PricingCreateRequest;

    addPricing(request);
  };

  const initialValues = {
    softwareLicenceId: softwareLicenceId,
    licenceType: softwareLicence![SoftwareLicenceSummaryKeys.LICENCE_TYPE],
  } as PricingFormModel;

  if (softwareLicence![SoftwareLicenceSummaryKeys.LICENCE_TYPE] === LicenceType.SINGLE_USER) {
    initialValues.pricingRules = [{ isLast: false }];
  } else if (softwareLicence![SoftwareLicenceSummaryKeys.LICENCE_TYPE] === LicenceType.CONCURRENT) {
    initialValues.pricingRules = [{ minRange: 1, isLast: false }];
  }

  return (
    <>
      <DialogHeader title={"Add Pricing"} />
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={pricingSchemeValidation}>
        {({ handleSubmit }) => (
          <CustomForm onSubmit={handleSubmit} padding="1rem 0" width="100%" overflow="hidden">
            <DialogBody>
              {softwareLicence && (
                <PricingForm licenceType={softwareLicence[SoftwareLicenceSummaryKeys.LICENCE_TYPE]} />
              )}
            </DialogBody>
            <DialogActionButtons loading={addLoading} buttonTitle={"Add"} />
          </CustomForm>
        )}
      </Formik>
    </>
  );
};

export default PricingDialog;
